import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import type { FrameAnnotationInfo } from '../../../studio-store/types';
import { buildAnnotationForOutput } from '../../../utils/build-annotation-for-output';
import { isFrameInAnySpan } from '../../../utils/frame-span-utils';
import { annotationLabelsAtom, annotationMetadataAtom } from '../../annotation-atoms';
import { studioStoreAtom } from '../../studio-store';
import type { AnnotationFrameWriteEntry } from '../../studio-store-write/actions';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import type { DeleteAnnotationsEventPayload } from '../events';
export const deleteAnnotationsCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<DeleteAnnotationsEventPayload, void> => {
  let deletedWrites: AnnotationFrameWriteEntry[] = [];
  return {
    execute: async payload => {
      const {
        currentFrame,
        annotationLabels
      } = payload;
      const studioStore = get(studioStoreAtom);
      const metadata = get(annotationMetadataAtom);
      const annotationLabelsList = get(annotationLabelsAtom);
      deletedWrites = [];
      if (!studioStore) {
        return;
      }
      if (currentFrame !== undefined) {
        for (const [id, meta] of metadata) {
          const matchesLabel = !annotationLabels?.length || annotationLabels.includes(meta.labelId);
          if (!matchesLabel) {
            continue;
          }
          if (!isFrameInAnySpan(currentFrame, meta.spans)) {
            continue;
          }
          const framePayload = await studioStore.getAnnotationFrameAtIndex(currentFrame, id);
          deletedWrites.push({
            frameIndex: currentFrame,
            annotationId: id,
            annotation: buildAnnotationForOutput(framePayload, meta, annotationLabelsList, currentFrame)
          });
        }
      } else if (annotationLabels?.length) {
        for (const [id, meta] of metadata) {
          if (!annotationLabels.includes(meta.labelId)) {
            continue;
          }
          const framePayloads = await studioStore.getAnnotationFramePayloadsInSpans(meta.spans, id);
          for (const span of meta.spans) {
            for (let frameIndex = span.start; frameIndex <= span.end; frameIndex++) {
              const framePayload = framePayloads.get(frameIndex);
              if (!framePayload) {
                continue;
              }
              deletedWrites.push({
                frameIndex,
                annotationId: id,
                annotation: buildAnnotationForOutput(framePayload, meta, annotationLabelsList, frameIndex)
              });
            }
          }
        }
      }
      if (!deletedWrites.length) {
        return;
      }
      const deletes: FrameAnnotationInfo[] = deletedWrites.map(({
        frameIndex,
        annotationId
      }) => ({
        frameIndex,
        annotationId
      }));
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
        deletes
      });
    },
    undo: () => {
      if (!deletedWrites.length) return;
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
        writes: deletedWrites
      });
    }
  };
};